import type {
	ButtonHTMLAttributes,
	InputHTMLAttributes,
	ReactNode,
	SelectHTMLAttributes,
	TextareaHTMLAttributes,
} from "react";
import { forwardRef, useState } from "react";
import { Link } from "react-router";

export function cx(...values: Array<string | false | null | undefined>): string {
	return values.filter(Boolean).join(" ");
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md";

const BUTTON_BASE =
	"inline-flex items-center justify-center gap-1.5 rounded-xl font-bold transition-[background-color,border-color,color,box-shadow,transform] active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0";

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
	primary: "bg-brand-600 text-white shadow-sm hover:bg-brand-700 hover:shadow-md",
	secondary: "border border-ink-300 bg-white text-ink-800 shadow-sm hover:bg-ink-50",
	ghost: "text-ink-600 hover:bg-ink-100 hover:text-ink-900",
	danger: "border border-danger-500 bg-white text-danger-600 hover:bg-danger-100",
};

const BUTTON_SIZE: Record<ButtonSize, string> = {
	sm: "min-h-9 px-3 py-1.5 text-[13px]",
	md: "min-h-11 px-4 py-2.5 text-[14px]",
};

export const Button = forwardRef<
	HTMLButtonElement,
	ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }
>(function Button({ variant = "primary", size = "md", className, ...rest }, ref) {
	return (
		<button
			ref={ref}
			type="button"
			className={cx(BUTTON_BASE, BUTTON_VARIANT[variant], BUTTON_SIZE[size], className)}
			{...rest}
		/>
	);
});

export function LinkButton({
	to,
	variant = "primary",
	size = "md",
	className,
	children,
}: {
	to: string;
	variant?: ButtonVariant;
	size?: ButtonSize;
	className?: string;
	children: ReactNode;
}) {
	return (
		<Link
			to={to}
			className={cx(BUTTON_BASE, BUTTON_VARIANT[variant], BUTTON_SIZE[size], className)}
		>
			{children}
		</Link>
	);
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({
	title,
	description,
	action,
	children,
	className,
}: {
	title?: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
	children?: ReactNode;
	className?: string;
}) {
	return (
		<section className={cx("ag-card", className)}>
			{(title || action) && (
				<header className="flex items-start justify-between gap-3 border-b border-ink-100 px-4 py-3">
					<div className="min-w-0">
						{title && <h2 className="text-[15px] font-bold text-ink-900">{title}</h2>}
						{description && <p className="mt-0.5 text-[13px] text-ink-500">{description}</p>}
					</div>
					{action && <div className="shrink-0">{action}</div>}
				</header>
			)}
			<div className="px-4 py-3">{children}</div>
		</section>
	);
}

export function PageHeader({
	eyebrow,
	title,
	description,
	actions,
}: {
	eyebrow?: ReactNode;
	title: ReactNode;
	description?: ReactNode;
	actions?: ReactNode;
}) {
	return (
		<header className="mb-5 flex flex-wrap items-end justify-between gap-3">
			<div className="min-w-0">
				{eyebrow && (
					<p className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-brand-600">
						{eyebrow}
					</p>
				)}
				<h1 className="text-[22px] font-bold tracking-tight text-ink-900">{title}</h1>
				{description && <p className="mt-1 max-w-2xl text-[14px] text-ink-600">{description}</p>}
			</div>
			{actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
		</header>
	);
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

export type Tone = "neutral" | "brand" | "success" | "warning" | "danger";

const TONE_CLASS: Record<Tone, string> = {
	neutral: "bg-ink-100 text-ink-600",
	brand: "bg-brand-100 text-brand-700",
	success: "bg-accent-100 text-accent-600",
	warning: "bg-warn-100 text-warn-600",
	danger: "bg-danger-100 text-danger-600",
};

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
	return <span className={cx("ag-chip", TONE_CLASS[tone])}>{children}</span>;
}

/** Workflow / Node / Approval など状態値の共通表示。未知状態も安全に表示する。 */
export function StatusBadge({ status }: { status: string }) {
	return <Badge tone={toneForStatus(status)}>{statusLabel(status)}</Badge>;
}

export function toneForStatus(status: string): Tone {
	switch (status) {
		case "SUCCEEDED":
		case "PASSED":
		case "ACTIVE":
		case "PUBLISHED":
		case "CONNECTED":
		case "APPROVED":
		case "COMPENSATED":
		case "accepted":
			return "success";
		case "RUNNING":
		case "QUEUED":
		case "READY":
		case "STARTING":
		case "EVALUATING":
		case "issued":
		case "implemented":
			return "brand";
		case "WAITING":
		case "PENDING":
		case "PAUSE_REQUESTED":
		case "SUSPENDED":
		case "RETRY_SCHEDULED":
		case "EXPIRING":
		case "REAUTH_REQUIRED":
		case "INVITED":
		case "DRAFT":
		case "CHANGES_REQUESTED":
		case "IN_PROGRESS":
		case "draft":
		case "verified":
		case "blocked":
			return "warning";
		case "FAILED":
		case "CANCELLED":
		case "CANCEL_REQUESTED":
		case "TIMED_OUT":
		case "REVOKED":
		case "ERROR":
		case "DISABLED":
		case "SUSPENDED_TENANT":
		case "REJECTED":
		case "EXPIRED":
		case "COMPENSATION_FAILED":
		case "rejected":
			return "danger";
		default:
			return "neutral";
	}
}

const STATUS_LABEL: Record<string, string> = {
	CREATED: "作成済み",
	QUEUED: "順番待ち",
	RUNNING: "作業中",
	WAITING: "待機中",
	PAUSE_REQUESTED: "一時停止要求",
	SUSPENDED: "一時停止",
	CANCEL_REQUESTED: "中止要求",
	COMPENSATING: "取り消し処理中",
	SUCCEEDED: "完了",
	FAILED: "失敗",
	CANCELLED: "中止",
	TIMED_OUT: "時間切れ",
	PENDING: "未着手",
	READY: "実行可能",
	RETRY_SCHEDULED: "再試行待ち",
	SKIPPED: "スキップ",
	PASSED: "合格",
	APPROVED: "承認済み",
	REJECTED: "却下",
	CHANGES_REQUESTED: "差戻し",
	EXPIRED: "期限切れ",
	ACTIVE: "有効",
	DISABLED: "無効",
	INVITED: "招待中",
	REVOKED: "取消済み",
	REAUTH_REQUIRED: "再認証が必要",
	EXPIRING: "まもなく期限切れ",
	ERROR: "エラー",
	DRAFT: "下書き",
	PUBLISHED: "公開中",
	DEPRECATED: "提供終了",
	CONNECTED: "接続済み",
	DISCONNECTED: "未接続",
	NEW: "未対応",
	IN_PROGRESS: "対応中",
	CLOSED: "完了",
	ARCHIVED: "アーカイブ",
	draft: "下書き",
	issued: "発注済み",
	implemented: "実装済み",
	verified: "検証済み",
	accepted: "受け入れ済み",
	rejected: "差し戻し",
	blocked: "裁定待ち",
};

export function statusLabel(status: string): string {
	return STATUS_LABEL[status] ?? status;
}

/** WAITINGの理由。End Userに内部語をそのまま出さない。 */
const WAIT_REASON_LABEL: Record<string, string> = {
	USER_INPUT: "あなたの入力待ち",
	USER_APPROVAL: "あなたの承認待ち",
	MANUAL_REVIEW: "担当者の確認待ち",
	QUALITY_REMEDIATION: "品質修正中",
	COST_APPROVAL: "費用の承認待ち",
	TIMER: "時間待ち",
	EXTERNAL_EVENT: "外部イベント待ち",
	CREDENTIAL_REFRESH: "AI接続の再認証待ち",
	CREDENTIAL_CAPACITY: "AI接続の空き待ち",
	QUOTA_RESET: "利用上限の回復待ち",
	RUNTIME_CAPACITY: "実行環境の空き待ち",
	PROVIDER_FALLBACK_APPROVAL: "AI切替の承認待ち",
	ADMIN_ACTION: "管理者の対応待ち",
};

export function waitReasonLabel(reason: string | null | undefined): string | null {
	if (!reason) return null;
	return WAIT_REASON_LABEL[reason] ?? "待機中";
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

export function Field({
	label,
	hint,
	error,
	children,
}: {
	label: string;
	hint?: ReactNode;
	error?: ReactNode;
	children: ReactNode;
}) {
	return (
		// biome-ignore lint/a11y/noLabelWithoutControl: 入力要素はchildrenとしてlabel内へ配置される
		<label className="block">
			<span className="ag-label">{label}</span>
			{hint && <span className="mb-1 mt-0.5 block text-[12px] text-ink-500">{hint}</span>}
			<span className="mt-1 block">{children}</span>
			{error && <span className="mt-1 block text-[12px] font-medium text-danger-600">{error}</span>}
		</label>
	);
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
	return <input {...props} className={cx("ag-input", props.className)} />;
}

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
	if (visible) {
		return (
			<svg viewBox="0 0 24 24" className="size-[18px]" fill="none" aria-hidden="true">
				<path
					d="M3.5 3.5 20.5 20.5M10.4 10.6a3 3 0 0 0 4.1 4.3M7.1 7.4C5 8.8 3.5 10.8 2.7 12c1.6 2.4 5.1 6.5 9.3 6.5 1.3 0 2.6-.4 3.7-1.1M16.7 16.2C18.7 14.8 20.1 12.9 21.3 12c-1.4-2.1-4.3-5.4-7.8-6.3"
					stroke="currentColor"
					strokeWidth="1.8"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		);
	}
	return (
		<svg viewBox="0 0 24 24" className="size-[18px]" fill="none" aria-hidden="true">
			<path
				d="M2.7 12c1.6-2.4 5.1-6.5 9.3-6.5S19.7 9.6 21.3 12c-1.6 2.4-5.1 6.5-9.3 6.5S4.3 14.4 2.7 12Z"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinejoin="round"
			/>
			<circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.8" />
		</svg>
	);
}

/** パスワード入力。右端の目アイコンで本文の表示／非表示を切り替える。 */
export function PasswordInput(props: InputHTMLAttributes<HTMLInputElement>) {
	const [visible, setVisible] = useState(false);
	return (
		<span className="relative block">
			<TextInput
				{...props}
				type={visible ? "text" : "password"}
				className={cx("pr-11", props.className)}
			/>
			<button
				type="button"
				className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-ink-500 hover:text-ink-900"
				aria-label={visible ? "パスワードを隠す" : "パスワードを表示"}
				aria-pressed={visible}
				onMouseDown={(event) => event.preventDefault()}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					setVisible((current) => !current);
				}}
			>
				<PasswordVisibilityIcon visible={visible} />
			</button>
		</span>
	);
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
	return <textarea {...props} className={cx("ag-input", props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
	return <select {...props} className={cx("ag-input", props.className)} />;
}

export function Toggle({
	checked,
	onChange,
	label,
	disabled,
	disabledReason,
}: {
	checked: boolean;
	onChange(next: boolean): void;
	label: string;
	disabled?: boolean;
	disabledReason?: string;
}) {
	return (
		<div>
			<label className="inline-flex cursor-pointer items-center gap-2">
				<input
					type="checkbox"
					checked={checked}
					disabled={disabled}
					onChange={(event) => onChange(event.target.checked)}
					className="size-4 accent-[var(--color-brand-600)]"
				/>
				<span className={cx("text-[14px]", disabled ? "text-ink-400" : "text-ink-800")}>
					{label}
				</span>
			</label>
			{disabled && disabledReason && (
				<p className="ml-6 text-[12px] text-ink-500">{disabledReason}</p>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Data display
// ---------------------------------------------------------------------------

export function DefinitionList({
	items,
	columns = 2,
}: {
	items: Array<{ label: string; value: ReactNode; mono?: boolean }>;
	columns?: 1 | 2 | 3;
}) {
	const gridClass =
		columns === 1 ? "sm:grid-cols-1" : columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
	return (
		<dl className={cx("grid grid-cols-1 gap-x-6 gap-y-3", gridClass)}>
			{items.map((item) => (
				<div key={item.label} className="min-w-0">
					<dt className="text-[12px] font-semibold uppercase tracking-wide text-ink-500">
						{item.label}
					</dt>
					<dd
						className={cx(
							"mt-0.5 break-words text-[14px] text-ink-900",
							item.mono && "font-mono text-[12.5px]",
						)}
					>
						{item.value}
					</dd>
				</div>
			))}
		</dl>
	);
}

export function Metric({
	label,
	value,
	unit,
	hint,
	tone = "neutral",
}: {
	label: string;
	value: ReactNode;
	unit?: string;
	hint?: ReactNode;
	tone?: Tone;
}) {
	return (
		<div className="ag-card px-4 py-3">
			<p className="text-[12px] font-semibold uppercase tracking-wide text-ink-500">{label}</p>
			<p className="mt-1 flex items-baseline gap-1">
				<span
					className={cx(
						"text-[26px] font-bold leading-none tracking-tight",
						tone === "danger"
							? "text-danger-600"
							: tone === "warning"
								? "text-warn-600"
								: "text-ink-900",
					)}
				>
					{value}
				</span>
				{unit && <span className="text-[13px] text-ink-500">{unit}</span>}
			</p>
			{hint && <p className="mt-1 text-[12px] text-ink-500">{hint}</p>}
		</div>
	);
}

export function Table({ head, children }: { head: ReactNode[]; children: ReactNode }) {
	return (
		<div className="overflow-x-auto">
			<table className="ag-table">
				<thead>
					<tr>
						{head.map((cell, index) => (
							// 列見出しは固定順で、実行時に並び替えないためindex keyで足りる
							// biome-ignore lint/suspicious/noArrayIndexKey: static header row
							<th key={index}>{cell}</th>
						))}
					</tr>
				</thead>
				<tbody>{children}</tbody>
			</table>
		</div>
	);
}

export function Callout({
	tone = "neutral",
	title,
	children,
}: {
	tone?: Tone;
	title?: ReactNode;
	children: ReactNode;
}) {
	const border: Record<Tone, string> = {
		neutral: "border-ink-200 bg-ink-50",
		brand: "border-brand-300 bg-brand-50",
		success: "border-accent-500/40 bg-accent-100",
		warning: "border-warn-500/40 bg-warn-100",
		danger: "border-danger-500/40 bg-danger-100",
	};
	return (
		<div className={cx("rounded-xl border px-4 py-3 shadow-state", border[tone])}>
			{title && <p className="text-[13px] font-bold text-ink-900">{title}</p>}
			<div className="text-[13px] leading-relaxed text-ink-700">{children}</div>
		</div>
	);
}
